import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTasksViewStore } from "../../../../stores/tasksViewStore";
import { Button, Card, Divider, EmptyState, Skeleton, TextInput, CustomPopover, SelectDropdown, showToast, SignupInterestCheckbox, Spinner, CustomTooltip } from "../../../../components/shared";
import { useIsFetching } from "@tanstack/react-query";
import { toUsDate, useDebounce } from "../../../../utils/utils";
import { Filter, ProjectsIcon, SearchIcon } from "../../../../assets/icons";
import { HiOutlineUserPlus, HiOutlineFolder, HiOutlineCog, HiOutlineDocumentText } from "react-icons/hi2";
import { HiOutlineClipboardDocumentList } from "react-icons/hi2";
import { PROJECT_GRADIENTS } from "../../../../constants/gradients";
import { useProjectTasks, useUpdateTaskStatus, useSwapTaskOrder, useProjectDetails } from "../../../../lib/react-query/tasks";
import GanttCalendarView from "../../../../components/Projects/GanttCalendarView";
import { getStatusLabel } from "../../../../constants/statuses";
import CreateTaskModal from "../../../../components/Projects/CreateTaskModal";
import InviteMemberModal from "../../../../components/Projects/InviteMemberModal";
import TaskCard from "../../../../components/Task/TaskCard";
import UpdateTaskModal from "../../../../components/Projects/UpdateTaskModal";
import { DndContext, DragOverlay, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useIsClient } from "../../../../hooks/useClientRole";
import { cn } from "../../../../lib/cn";
import { COLORS } from "../../../../constants/colors";


const Column = ({ id, title, items, children }) => {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`border rounded-xl bg-white p-3 border-t-2 ${isOver ? "ring-2 ring-secondary/30" : ""} ${id === 'pending' ? 'border-t-warning/60' : id === 'active' ? 'border-t-secondary/60' : 'border-t-success/60'}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-sans text-sm font-semibold">{title}</p>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-darkgrey">
          <span className={`w-1.5 h-1.5 rounded-full ${id === 'pending' ? 'bg-warning' : id === 'active' ? 'bg-secondary' : 'bg-success/100'}`} />
          {items?.length || 0}
        </span>
      </div>
      <div className="flex flex-col gap-3 min-h-[200px]">{children}</div>
    </div>
  );
};

const DraggableTask = ({ task, container, onOpen }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: `task-${task?.id}`, data: { container, taskId: task?.id } });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1, cursor: "grab" };
  return (
    <div ref={setNodeRef} style={style} className={cn(isDragging && "z-toast relative")} {...listeners} {...attributes}>
      <TaskCard task={task} onClick={() => onOpen?.(task)} />
    </div>
  );
};

const DaysProgress = ({ startDate, endDate }) => {
  const s = startDate ? new Date(startDate) : null;
  const e = endDate ? new Date(endDate) : null;
  if (!s || !e) {
    return (
      <div className="flex items-center gap-3">
        <svg width={56} height={56} className="text-secondary-dark">
          <circle cx={28} cy={28} r={24} stroke={COLORS.surfaceBorder} strokeWidth={6} fill="none" />
        </svg>
        <div>
          <p className="text-xs text-grey">Days Used / Total</p>
          <p className="text-sm">—</p>
        </div>
      </div>
    );
  }
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const duration = Math.max(0, Math.ceil((e - s) / msPerDay));
  const rawUsed = Math.ceil((now - s) / msPerDay);
  const used = Math.max(0, Math.min(duration, rawUsed));
  const percent = duration ? Math.round((used / duration) * 100) : 0;
  const size = 56;
  const strokeWidth = 6;
  const r = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - percent / 100);
  return (
    <div className="flex items-center gap-3">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={COLORS.surfaceBorder} strokeWidth={strokeWidth} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={COLORS.secondary} strokeWidth={strokeWidth} fill="none" strokeDasharray={`${circumference} ${circumference}`} strokeDashoffset={dashOffset} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div>
        <p className="text-xs text-grey">Days Used / Total</p>
        <p className="text-sm">{`${used}d / ${duration}d`}</p>
      </div>
    </div>
  );
};

const ProjectTasks = () => {
  const navigate = useNavigate();
  const { id: idParam, taskId: routeTaskId } = useParams();
  const numericId = Number(idParam);
  const isValidProjectId = Number.isFinite(numericId);
  const { filters, pages, setSearch, setOrdering, setTagsSelected, setPage, resetPages } = useTasksViewStore();
  const [sizes] = useState({ pending: 20, started: 20, done: 20 });
  const [openPopover, setOpenPopover] = useState(false);
  const [openSort, setOpenSort] = useState(false);
  const [filterForm, setFilterForm] = useState({ ordering: "sort_index" });
  const [activeId, setActiveId] = useState(null);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 8 },
  });
  const sensors = useSensors(pointerSensor);
  const [viewMode, setViewMode] = useState("board");
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openInviteModal, setOpenInviteModal] = useState(false);
  const [filterSelectedTags, setFilterSelectedTags] = useState([]);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const isFetching = useIsFetching();
  const { isClient: isClientModeActive } = useIsClient();


  const debouncedSearch = useDebounce(filters?.search || "", 300);
  useEffect(() => {
    resetPages();
  }, [debouncedSearch, filters?.ordering, filters?.tagsSelected, resetPages]);
  const params = useMemo(() => ({
    search: debouncedSearch,
    ordering: filters?.ordering || "sort_index",
    tags_filter: Array.isArray(filters?.tagsSelected) ? filters.tagsSelected.join(",") : "",
    page_pending: pages?.pending || 1,
    page_size_pending: sizes.pending,
    page_started: pages?.started || 1,
    page_size_started: sizes.started,
    page_done: pages?.done || 1,
    page_size_done: sizes.done,
  }), [debouncedSearch, filters?.ordering, filters?.tagsSelected, pages, sizes]);

  const { data: tasksData, isLoading } = useProjectTasks(isValidProjectId ? numericId : null, params, isValidProjectId);
  const { data: project } = useProjectDetails(isValidProjectId ? numericId : null, isValidProjectId);
  const { mutateAsync: updateTaskStatus } = useUpdateTaskStatus();
  const { mutateAsync: swapTaskOrder } = useSwapTaskOrder();

  const [board, setBoard] = useState({ pending: [], active: [], done: [] });
  const [acc, setAcc] = useState({ pending: [], active: [], done: [] });
  const lastOpenedTaskRef = useRef(null);

  const totalTasks = board.pending.length + board.active.length + board.done.length;
  const hasTaskFilters = Boolean(filters?.search) || filterSelectedTags.length > 0;
  const isBoardEmpty = !isLoading && totalTasks === 0;
  const hasProjectDetails = Boolean(
    project?.description || project?.status || project?.created_at || project?.start_date
  );
  const showOverview = totalTasks > 0 || hasProjectDetails;
  const showTaskCounts = totalTasks > 0;
  const completionPercentage = totalTasks > 0 ? Math.round((board.done.length / totalTasks) * 100) : 0;
  const hasDateRange = Boolean(project?.start_date && project?.end_date);
  const projectGradient = PROJECT_GRADIENTS[project?.header_gradient] || "from-primary to-secondary-dark";

  useEffect(() => {
    const mergeUnique = (prev, next) => {
      const map = new Map();
      [...prev, ...next].forEach((t) => map.set(String(t.id), t));
      return Array.from(map.values());
    };
    const nextPending = tasksData?.pending?.items || [];
    const nextActive = tasksData?.active?.items || [];
    const nextDone = tasksData?.done?.items || [];
    setAcc((prev) => ({
      pending: (pages?.pending || 1) > 1 ? mergeUnique(prev.pending, nextPending) : nextPending,
      active: (pages?.started || 1) > 1 ? mergeUnique(prev.active, nextActive) : nextActive,
      done: (pages?.done || 1) > 1 ? mergeUnique(prev.done, nextDone) : nextDone,
    }));
  }, [tasksData?.pending?.items, tasksData?.active?.items, tasksData?.done?.items, pages?.pending, pages?.started, pages?.done]);

  useEffect(() => {
    setBoard(acc);
  }, [acc]);

  useEffect(() => {
    if (routeTaskId && routeTaskId !== lastOpenedTaskRef.current) {
      lastOpenedTaskRef.current = routeTaskId;
      setOpenUpdateModal(true);
      const sid = String(routeTaskId);
      const t = board.pending.find((x) => String(x.id) === sid)
        || board.active.find((x) => String(x.id) === sid)
        || board.done.find((x) => String(x.id) === sid);
      setSelectedTask(t || null);
    } else if (!routeTaskId) {
      lastOpenedTaskRef.current = null;
      setOpenUpdateModal(false);
      setSelectedTask(null);
    }
  }, [routeTaskId, board.pending, board.active, board.done]);

  const aggregatedTags = useMemo(() => {
    const raw = [];
    for (const g of [acc.pending, acc.active, acc.done]) {
      for (const t of g || []) {
        const tags = Array.isArray(t?.tags) ? t.tags : [];
        for (const name of tags) {
          const s = String(name || "").trim();
          if (s) raw.push(s);
        }
      }
    }
    return Array.from(new Set(raw));
  }, [acc.pending, acc.active, acc.done]);

  const taskTagOptions = useMemo(() => Array.from(new Set([...(aggregatedTags || [])])), [aggregatedTags]);

  useEffect(() => {
    setFilterForm((p) => ({ ...p, ordering: filters?.ordering || "sort_index" }));
    setFilterSelectedTags(Array.isArray(filters?.tagsSelected) ? filters.tagsSelected : []);
  }, [filters?.ordering, filters?.tagsSelected, project]);

  const handleDragEnd = async (event) => {
    if (isClientModeActive) return;
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const taskId = String(active?.id || "").replace("task-", "");
    const from = active?.data?.current?.container;
    let to = over?.id;
    if (!taskId || !from || !to) return;

    const findContainerByTaskId = (tid) => {
      const sid = String(tid);
      if (board.pending.some((t) => String(t.id) === sid)) return "pending";
      if (board.active.some((t) => String(t.id) === sid)) return "active";
      if (board.done.some((t) => String(t.id) === sid)) return "done";
      return null;
    };

    if (String(to).startsWith("task-")) {
      const targetTaskId = String(to).replace("task-", "");
      to = findContainerByTaskId(targetTaskId) || to;
    }

    if (from !== to && (to === "pending" || to === "active" || to === "done")) {
      const next = { ...board };
      const source = next[from].filter((t) => String(t.id) !== String(taskId));
      const moved = board[from].find((t) => String(t.id) === String(taskId));
      const targetArr = next[to];
      const updatedTarget = [...targetArr, moved];
      next[from] = source;
      next[to] = updatedTarget;
      setBoard(next);
      const status = to === "pending" ? "PENDING" : to === "active" ? "ACTIVE" : "DONE";
      try {
        await updateTaskStatus({ taskId, payload: { status }, projectId: numericId });
      } catch (e) {
        const msg = e?.response?.data?.message
        showToast(msg, "error", { position: "top-center" });
      }
      return;
    }

    if (from === to && (to === "pending" || to === "active" || to === "done")) {
      const activeIndex = board[from].findIndex((t) => String(t.id) === String(taskId));
      let overIndex = activeIndex;
      if (String(over?.id).startsWith("task-")) {
        const targetId = String(over?.id).replace("task-", "");
        overIndex = board[from].findIndex((t) => String(t.id) === String(targetId));
      }
      if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) return;
      const movedColumn = arrayMove(board[from], activeIndex, overIndex);
      setBoard((prev) => ({ ...prev, [from]: movedColumn }));
      try {
        const first_id = Number(taskId);
        const second_id = Number(board[from][overIndex]?.id);
        if (first_id && second_id) {
          await swapTaskOrder({ projectId: numericId, first_id, second_id });
        }
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to reorder";
        showToast(msg, "error", { position: "top-center" });
      }
    }
  };

  return (
    <div>
      <div className="bg-bggrey pt-4 sticky top-0 z-50 rounded-b-xl">
        <div className="bg-white rounded-xl">
          <div className="flex flex-col sm:flex-row w-full justify-between items-center gap-4 sm:gap-0 px-4 py-3">
            <div className="font-sans flex items-center gap-3 w-full sm:w-auto">
              <ProjectsIcon width={30} height={30} className="text-black flex-shrink-0" />
              <div className="flex min-w-0 items-center gap-2">
                {project?.team ? (
                  <Link
                    to={`/teams/${project.team}`}
                    className="text-sm text-grey transition hover:text-secondary-dark"
                  >
                    {project.team_name || "Team"}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="text-sm text-grey transition hover:text-secondary-dark"
                    onClick={() => navigate("/projects")}
                  >
                    Projects
                  </button>
                )}
                <span className="text-grey">/</span>
                <CustomTooltip content={project?.title || "—"}>
                  <span className="text-sm font-semibold max-w-[150px] truncate inline-block align-bottom">{project?.title || '—'}</span>
                </CustomTooltip>
                <span className="text-grey">/</span>
                <span className="text-sm">Overview</span>
              </div>
            </div>
            {isClientModeActive ? (
              <div className="font-sans flex w-full sm:w-auto sm:items-center sm:justify-end">
                <Button onClick={() => navigate(`/projects/${numericId}/files`)} variant="outlined" color="secondary" size="md" icon={HiOutlineFolder} className="w-full sm:w-auto justify-center">Files</Button>
              </div>
            ) : (
              <div className="font-sans grid grid-cols-2 w-full gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center sm:justify-end">
                <Button onClick={() => setOpenInviteModal(true)} variant="outlined" color="secondary" size="md" icon={HiOutlineUserPlus} className="gap-2 w-full sm:w-auto justify-center">Invite Member</Button>
                <Button onClick={() => navigate(`/projects/${numericId}/files`)} variant="outlined" color="secondary" size="md" icon={HiOutlineFolder} className="gap-2 w-full sm:w-auto justify-center">Files</Button>
                <Button onClick={() => navigate(`/projects/${numericId}/draft-flow`)} variant="outlined" color="secondary" size="md" icon={HiOutlineDocumentText} className="gap-2 w-full sm:w-auto justify-center">Draft Flow</Button>
                <Button onClick={() => navigate(`/projects/${numericId}/settings`)} variant="outlined" color="secondary" size="md" icon={HiOutlineCog} className="gap-2 w-full sm:w-auto justify-center">Settings</Button>
              </div>
            )}
          </div>
          <div className="px-2"><Divider /></div>
        </div>
      </div>

      {showOverview && (
        <Card className="mt-6 overflow-hidden">
          <div className={`relative overflow-hidden bg-gradient-to-r ${projectGradient} px-4 py-4`}>
            <div className="relative z-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15 text-white ring-1 ring-white/25">
                  <ProjectsIcon width={21} height={21} />
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/75">
                    {isClientModeActive ? "Shared project" : "Project overview"}
                  </p>
                  <h2 className="truncate text-lg font-semibold text-white sm:text-xl">
                    {project?.title || "Project overview"}
                  </h2>
                  {isClientModeActive && (
                    <p className="mt-1 text-xs text-white/80">
                      Track the work shared with you and the next steps from the project team.
                    </p>
                  )}
                </div>
              </div>
              <span className="w-fit rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25">
                {getStatusLabel(project?.status)}
              </span>
            </div>
          </div>

          <div className="space-y-4 p-4">
            {showTaskCounts && (
              <>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div className="rounded-lg border border-surface-border border-l-2 border-l-warning/60 bg-surface/60 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Pending</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-primary">{board.pending.length}</p>
                  </div>
                  <div className="rounded-lg border border-surface-border border-l-2 border-l-secondary/60 bg-surface/60 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Active</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-primary">{board.active.length}</p>
                  </div>
                  <div className="rounded-lg border border-surface-border border-l-2 border-l-success/60 bg-surface/60 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Done</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-primary">{board.done.length}</p>
                  </div>
                  <div className="rounded-lg border border-surface-border border-l-2 border-l-primary/40 bg-surface/60 p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Completion</p>
                    <p className="mt-1 text-3xl font-bold tabular-nums text-primary">{completionPercentage}%</p>
                  </div>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-lightgrey">
                  <div className="h-full rounded-full bg-secondary transition-all" style={{ width: `${completionPercentage}%` }} />
                </div>
              </>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-surface-border bg-surface/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Description</p>
                <p className="mt-1 text-sm whitespace-normal break-words [overflow-wrap:anywhere]">{project?.description || "—"}</p>
              </div>
              <div className="rounded-lg border border-surface-border bg-surface/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Status</p>
                <p className="mt-1 text-sm font-semibold">{getStatusLabel(project?.status)}</p>
              </div>
              <div className="rounded-lg border border-surface-border bg-surface/40 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-grey">Date created</p>
                <p className="mt-1 text-sm font-semibold tabular-nums">{project?.created_at ? toUsDate(project.created_at) : "—"}</p>
              </div>
              {hasDateRange ? (
                <div className="rounded-lg border border-surface-border bg-surface/40 p-3">
                  <DaysProgress startDate={project?.start_date} endDate={project?.end_date} />
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      )}


      <div className="mt-4 rounded-xl border border-surface-border bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          {!isClientModeActive && (
            <Button className="shrink-0 whitespace-nowrap" onClick={() => setOpenCreateModal(true)} color="primary" size="md">Create Task</Button>
          )}
          <div className="flex shrink-0 items-center gap-1 rounded-lg border border-surface-border p-0.5">
            <button
              type="button"
              aria-pressed={viewMode === 'board'}
              className={`rounded-md px-3 py-1 text-sm transition ${viewMode === 'board' ? 'bg-secondary text-white' : 'text-darkgrey hover:bg-surface-hover'}`}
              onClick={() => setViewMode('board')}
            >
              Board
            </button>
            <button
              type="button"
              aria-pressed={viewMode === 'calendar'}
              className={`rounded-md px-3 py-1 text-sm transition ${viewMode === 'calendar' ? 'bg-secondary text-white' : 'text-darkgrey hover:bg-surface-hover'}`}
              onClick={() => setViewMode('calendar')}
            >
              Calendar
            </button>
          </div>
          {viewMode === 'board' && !(isBoardEmpty && !hasTaskFilters) && (
          <>
          <div className="min-w-[190px] flex-1 max-w-sm">
              <TextInput
                label=""
                placeholder="Search tasks"
                type="text"
                name="search"
                value={filters?.search || ''}
                onChange={(e) => setSearch(e.target.value)}
                startContent={<SearchIcon width={16} height={16} className="text-darkgrey" />}
              />
            </div>
          <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
            <CustomPopover
              open={openSort}
              setOpen={setOpenSort}
              placement="bottom-start"
              trigger={
                <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-darkgrey px-3 py-1.5 text-sm font-sans font-semibold text-darkgrey transition-colors hover:bg-surface-hover">
                  <span>{filters?.ordering === 'due_date' ? 'Sort: Due' : filters?.ordering === 'priority' ? 'Sort: Priority' : 'Sort'}</span>
                </button>
              }
            >
              <div className="flex flex-col py-1 min-w-[140px]">
                {[
                  { value: 'sort_index', label: 'Sort' },
                  { value: 'due_date', label: 'Sort: Due' },
                  { value: 'priority', label: 'Sort: Priority' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => { setOrdering(opt.value); setOpenSort(false); }}
                    className={`w-full text-left px-3 py-2 text-sm font-sans transition-colors cursor-pointer ${filters?.ordering === opt.value ? 'bg-secondary/10 text-secondary-dark font-semibold' : 'text-darkgrey hover:bg-surface-hover'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </CustomPopover>
            <div>
              <CustomPopover
                open={openPopover}
                setOpen={setOpenPopover}
                placement="bottom-end"
                trigger={
                  <button type="button" className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-darkgrey transition-colors hover:bg-surface-hover">
                    <Filter />
                    <span className="text-sm font-semibold">Filter</span>
                  </button>
                }
              >
                <div className="gap-3 w-80 flex flex-col items-center bg-white p-2 rounded-md ">
                  <div className="flex flex-col gap-3 w-full border-b border-surface-border pb-2">
                    <div className="vertical-center justify-between">
                      <p className="font-sans font-semibold text-sm">Filter Options</p>
                      <button type="button" onClick={() => { setFilterForm({ ordering: 'sort_index' }); setFilterSelectedTags([]); }} className="font-sans cursor-pointer font-semibold text-sm text-secondary-dark">Clear</button>
                    </div>
                    <div className="flex flex-col gap-4 overflow-auto h-[300px]">
                      <SelectDropdown
                        options={[
                          { id: 'sort_index', name: 'Sort Index' },
                          { id: '-sort_index', name: '- Sort Index' },
                          { id: 'title', name: 'Title' },
                          { id: '-title', name: '- Title' },
                          { id: 'priority', name: 'Priority' },
                          { id: '-priority', name: '- Priority' },
                          { id: 'due_date', name: 'Due Date' },
                          { id: '-due_date', name: '- Due Date' },
                          { id: 'created_at', name: 'Created At' },
                          { id: '-created_at', name: '- Created At' },
                          { id: 'updated_at', name: 'Updated At' },
                          { id: '-updated_at', name: '- Updated At' },
                          { id: 'status', name: 'Status' },
                          { id: '-status', name: '- Status' },
                        ]}
                        label="Ordering"
                        name="ordering"
                        value={filterForm.ordering}
                        onChange={(e) => setFilterForm((p) => ({ ...p, ordering: e.target.value }))}
                      />
                      <div>
                        <p className="font-normal text-sm pb-1">Tags</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(aggregatedTags || []).map((tag) => {
                            const checked = filterSelectedTags.includes(tag);
                            return (
                              <SignupInterestCheckbox
                                key={tag}
                                name={tag}
                                value={checked}
                                onChange={() => {
                                  setFilterSelectedTags((prev) => checked ? prev.filter((t) => t !== tag) : [...prev, tag]);
                                }}
                              >
                                {tag}
                              </SignupInterestCheckbox>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p
                    className={`text-sm pb-2 font-medium transition-colors duration-200 ease-in-out text-secondary-dark hover:text-secondary-dark hover:underline cursor-pointer`}
                    onClick={() => {
                      const nextTags = filterSelectedTags;
                      setOrdering(filterForm.ordering);
                      setTagsSelected(nextTags);
                      resetPages();
                      setOpenPopover(false);
                    }}
                  >
                    Apply Filters
                  </p>
                </div>
              </CustomPopover>
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <GanttCalendarView projectId={numericId} />
      ) : isLoading && board.pending.length === 0 && board.active.length === 0 && board.done.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pb-6 mt-6">
          {Array.from({ length: 9 }, (_, i) => (<Skeleton key={i} className="w-full h-24 rounded-3xl" />))}
        </div>
      ) : isBoardEmpty ? (
        <div className="mt-6">
          {hasTaskFilters ? (
            <EmptyState
              icon={HiOutlineClipboardDocumentList}
              title="No tasks match your filters"
              description="Try adjusting your search or clearing the filters."
            />
          ) : (
            <EmptyState
              icon={HiOutlineClipboardDocumentList}
              title="No tasks yet"
              description={isClientModeActive
                ? "Tasks shared by the project team will appear here."
                : "Create your first task to start tracking progress on this project."}
              actionText={!isClientModeActive ? "Create Task" : undefined}
              onAction={!isClientModeActive ? () => setOpenCreateModal(true) : undefined}
            />
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={handleDragEnd} onDragCancel={() => setActiveId(null)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 items-start gap-4 pb-6 mt-6">
            <Column id="pending" title="Pending" items={board.pending}>
              <SortableContext items={board.pending.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.pending?.length > 0 ? board.pending.map((task) => (
                  <DraggableTask
                    key={task.id}
                    task={task}
                    container="pending"
                    activeId={activeId}
                    onOpen={(t) => navigate(`/projects/${numericId}/tasks/${t.id}`)}
                  />
                )) : <EmptyState title="No Pending Tasks" compact />}
              </SortableContext>
              {tasksData?.pending?.page < tasksData?.pending?.total_pages ? (
                <div className="mt-3 center">
                  <button className="px-3 py-1 rounded-md border border-surface-border hover:border-primary/30 hover:text-primary transition-colors text-sm" onClick={() => setPage('pending', (pages?.pending || 1) + 1)}>Load more</button>
                </div>
              ) : null}
            </Column>
            <Column id="active" title="Active" items={board.active}>
              <SortableContext items={board.active.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.active?.length > 0 ? board.active.map((task) => (
                  <DraggableTask
                    key={task.id}
                    task={task}
                    container="active"
                    activeId={activeId}
                    onOpen={(t) => navigate(`/projects/${numericId}/tasks/${t.id}`)}
                  />
                )) : <EmptyState title="No Active Tasks" compact />}
              </SortableContext>
              {tasksData?.active?.page < tasksData?.active?.total_pages ? (
                <div className="mt-3 center">
                  <button className="px-3 py-1 rounded-md border border-surface-border hover:border-primary/30 hover:text-primary transition-colors text-sm" onClick={() => setPage('started', (pages?.started || 1) + 1)}>Load more</button>
                </div>
              ) : null}
            </Column>
            <Column id="done" title="Done" items={board.done}>
              <SortableContext items={board.done.map((t) => `task-${t.id}`)} strategy={verticalListSortingStrategy}>
                {board.done?.length > 0 ? board.done.map((task) => (
                  <DraggableTask
                    key={task.id}
                    task={task}
                    container="done"
                    activeId={activeId}
                    onOpen={(t) => navigate(`/projects/${numericId}/tasks/${t.id}`)}
                  />
                )) : <EmptyState title="No Done Tasks" compact />}
              </SortableContext>
              {tasksData?.done?.page < tasksData?.done?.total_pages ? (
                <div className="mt-3 center">
                  <button className="px-3 py-1 rounded-md border border-surface-border hover:border-primary/30 hover:text-primary transition-colors text-sm" onClick={() => setPage('done', (pages?.done || 1) + 1)}>Load more</button>
                </div>
              ) : null}
            </Column>
          </div>
          <DragOverlay>
            {activeId ? (() => {
              const sid = String(activeId).replace("task-", "");
              const t = board.pending.find((x) => String(x.id) === sid) || board.active.find((x) => String(x.id) === sid) || board.done.find((x) => String(x.id) === sid);
              return t ? <TaskCard task={t} onClick={() => navigate(`/projects/${numericId}/tasks/${t.id}`)} /> : null;
            })() : null}
          </DragOverlay>
        </DndContext>
      )}

      <CreateTaskModal isOpen={openCreateModal} onClose={() => setOpenCreateModal(false)} projectId={numericId} tagOptions={taskTagOptions} />
      <InviteMemberModal isOpen={openInviteModal} onClose={() => setOpenInviteModal(false)} projectId={numericId} />



      <UpdateTaskModal isOpen={openUpdateModal} onClose={() => { setOpenUpdateModal(false); navigate(`/projects/${numericId}/tasks`); }} projectId={numericId} taskId={routeTaskId || selectedTask?.id} initialTask={selectedTask} readOnly={isClientModeActive} />

      {isFetching ? (
        <div className="fixed bottom-4 right-4 z-50">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
};

export default ProjectTasks;
